import { z } from 'zod';
import { businessSchema } from '../../common/app-registry.schema';
import { currencySchema } from '../../common/currency.schema';
import { percentageSchema, positiveIntegerSchema, pricePositiveSchema } from '../../validation';

/**
 * When a reward fires.
 *
 * - `none`       — track only. Views, clicks, leads and paid conversions
 *                  are counted on the link, nothing is ever owed.
 * - `first_paid` — once per referred account: the first settled Billing
 *                  of a referred party creates the reward, later Billings
 *                  do not.
 * - `every_paid` — every settled Billing of a referred party creates a
 *                  reward (a running cut of the invoice).
 * - `per_click`  — every deduped `click` hit on the link creates a
 *                  reward (pay-per-click marketing). No Billing is
 *                  involved, so `amount_percent` parts have no base and
 *                  are skipped; price clicks with `amount_fixed` or
 *                  `order_credits`.
 *
 * A single field rather than booleans so a fifth shape (`first_n_paid`,
 * a time window) slots in as a new value without a schema migration.
 */
export const referralRewardTriggerSchema = z.enum([
  'none',
  'first_paid',
  'every_paid',
  'per_click',
]);
export type ReferralRewardTrigger = z.infer<typeof referralRewardTriggerSchema>;

/**
 * A fixed cash amount owed to the partner per trigger — "10 SAR per paid
 * user", "500 SAR per paid org". Paid off-platform; the reward row is
 * the ledger entry the finance team marks `paid`.
 */
export const referralRewardPartAmountFixedSchema = z.object({
  kind: z.literal('amount_fixed'),
  /** Cash owed per trigger. */
  amount: pricePositiveSchema,
  currency: currencySchema.default('SAR'),
});

/**
 * A percentage of the settled Billing's `amount.subtotal` (pre-tax).
 * Stored as a fraction — `0.10` is 10% — like every other percentage in
 * the schema (`percentageSchema`). The admin UI shows and edits percent.
 */
export const referralRewardPartAmountPercentSchema = z.object({
  kind: z.literal('amount_percent'),
  /** Fraction of `Billing.amount.subtotal`: `0.10` = 10%. */
  percent: percentageSchema,
  /**
   * Optional ceiling per reward row, in the Billing's currency. Absent
   * means uncapped.
   */
  maxAmount: pricePositiveSchema.optional(),
});

/**
 * Free platform credits granted to the partner's own usage subscription
 * — "3 more وثائق for every paid user you bring". Requires the partner
 * to own an account (`ReferralPartner.owner`); a partner with no account
 * cannot receive credits, and the reward evaluator skips the part with
 * a `skipped` reason rather than failing the whole reward.
 *
 * `business` says which credit bucket to top up; the display noun comes
 * from `BUSINESS_REGISTRY[business].creditUnit`. Granted through
 * `SubscriptionCreditsService.grantCredits(..., mode: 'free')`, which
 * opens a usage bucket for the owner when none exists.
 */
export const referralRewardPartOrderCreditsSchema = z.object({
  kind: z.literal('order_credits'),
  /** Which brand's credit bucket receives the units. */
  business: businessSchema,
  /** Units granted per trigger. */
  units: positiveIntegerSchema.refine(v => v > 0, 'units must be > 0'),
  /** Optional expiry, in days from the grant. Absent = never expires. */
  expiresInDays: positiveIntegerSchema.optional(),
});

/**
 * One reward component. A rule composes several — "10 SAR AND 2 free
 * credits" is two parts on one rule — so the reward can be cash, credits,
 * or a percentage cut, or any mix, without a second rule type.
 */
export const referralRewardPartSchema = z.discriminatedUnion('kind', [
  referralRewardPartAmountFixedSchema,
  referralRewardPartAmountPercentSchema,
  referralRewardPartOrderCreditsSchema,
]);
export type ReferralRewardPart = z.infer<typeof referralRewardPartSchema>;
export type ReferralRewardPartKind = ReferralRewardPart['kind'];

/** What one business pays — the components owed per trigger under it. */
export const referralRewardPricingSchema = z.object({
  business: businessSchema,
  parts: z.array(referralRewardPartSchema).default([]),
});
export type ReferralRewardPricing = z.infer<typeof referralRewardPricingSchema>;

/**
 * What a partner earns and when.
 *
 * Lives on `ReferralPartner.reward` as the default and optionally on
 * `ReferralLink.reward` as a per-link override (a link for a promo can
 * pay differently from the partner's standing deal). The evaluator uses
 * `link.reward ?? partner.reward`.
 */
export const referralRewardRuleSchema = z.object({
  /** When the rule fires. Defaults to once per referred account. */
  trigger: referralRewardTriggerSchema.default('first_paid'),
  /**
   * One pricing entry per business the partner markets (Hero AI, Wathiqa
   * Plus, …). A settled Billing is priced with the entry matching
   * `Billing.purchase.business`; a click with the entry matching the
   * link's / page's business. No entry, or an entry with no parts, means
   * that business is tracked but pays nothing. Business-level on purpose
   * — every plan and addon under a business inherits its pricing the day
   * it ships. The same parts repeated under two businesses is the normal
   * "same deal everywhere" shape; there is no shared default to inherit.
   */
  pricing: z.array(referralRewardPricingSchema).default([]),
});
export type ReferralRewardRule = z.infer<typeof referralRewardRuleSchema>;
